import { useEffect } from 'react';

const DEFAULT_TITLE = 'Best School in Partapur, Banswara | Maxfort Education School';
const DEFAULT_DESCRIPTION =
  'Maxfort Education School in Partapur, Banswara, Rajasthan offers quality English medium education, admissions support, campus facilities, and a student-focused learning environment.';
const DEFAULT_KEYWORDS =
  'school in Partapur, best school in Partapur, school in Banswara, best school in Banswara, Rajasthan school, English medium school in Partapur, CBSE school in Banswara, school admission in Partapur, school admission in Banswara, private school in Partapur, senior secondary school in Banswara, top school in Partapur Rajasthan';

const SITE_NAME = 'Maxfort Education School';
const SITE_URL = process.env.REACT_APP_SITE_URL || '';
const DEFAULT_IMAGE = '/external/images/cover.jpeg';

const setMetaTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const setLinkTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const setJsonLd = (id, data) => {
  let element = document.head.querySelector(`#${id}`);

  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
};

const buildAbsoluteUrl = (path) => {
  const origin = SITE_URL || window.location.origin;
  return new URL(path || window.location.pathname, origin).toString();
};

const Seo = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  path = '/',
  image = DEFAULT_IMAGE,
  schema
}) => {
  useEffect(() => {
    const canonicalUrl = buildAbsoluteUrl(path);
    const imageUrl = buildAbsoluteUrl(image);

    document.title = title;

    setMetaTag('meta[name="description"]', { name: 'description', content: description });
    setMetaTag('meta[name="keywords"]', { name: 'keywords', content: keywords });
    setMetaTag('meta[name="robots"]', { name: 'robots', content: 'index, follow, max-image-preview:large' });

    setMetaTag('meta[property="og:title"]', { property: 'og:title', content: title });
    setMetaTag('meta[property="og:description"]', { property: 'og:description', content: description });
    setMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    setMetaTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    setMetaTag('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    setMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });

    setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });

    setLinkTag('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });

    if (schema) {
      setJsonLd('page-seo-schema', schema(canonicalUrl, imageUrl));
    }
  }, [title, description, keywords, path, image, schema]);

  return null;
};

export const schoolSchema = (canonicalUrl, imageUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'School',
  name: SITE_NAME,
  url: canonicalUrl,
  image: imageUrl,
  logo: buildAbsoluteUrl('/external/images/logo1.png'),
  telephone: '+91 9928620516',
  email: 'maxforteducationschool@gmail.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Bedwa Lamps Street, Near Vaghar Nagar',
    addressLocality: 'Partapur',
    addressRegion: 'Rajasthan',
    postalCode: '327024',
    addressCountry: 'IN'
  },
  areaServed: ['Partapur', 'Banswara', 'Rajasthan'],
  openingHours: 'Mo-Sa 08:00-14:00',
  sameAs: [
    'https://www.facebook.com/share/A2Td7dTwSd5BhD4W/?mibextid=qi2Omg',
    'https://www.instagram.com/maxfort_educationschool?igsh=N3V4eWRhZzFsc3J4'
  ]
});

export const webPageSchema = (name, description) => (canonicalUrl, imageUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url: canonicalUrl,
  primaryImageOfPage: imageUrl,
  isPartOf: {
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL || window.location.origin
  }
});

export default Seo;
